import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import {USER_KEYS} from "@/api/userApi";
import CrossIcon from "@/assets/FF5A4F_cross_icon.svg?react";
import PlusIcon from "@/assets/FFFFFF_plus_icon.svg?react";
import EditIcon from "@/assets/colorable/pen_edit.svg?react";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useAgencyId, useIsAgency, useSelectedCompanyId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {AbilityContext} from "@/permissions";
import useCompanyStore from "@/store/company";
import useRegistrationStore from "@/store/registration";
import useTokenStore from "@/store/token";
import useUserStore from "@/store/user";
import {handleErrorWithToast} from "@/utils/errorHandlers";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import {type FC, memo, useContext, useEffect, useMemo, useState} from "react";
import {useDropzone} from "react-dropzone";
import {useSearchParams} from "react-router";

const BrandLogo: FC<{
  isCompanyProfile?: boolean;
}> = ({isCompanyProfile = false}) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const accessTokenFromUrl = searchParams.get("access_token");
  const abilityContext = useContext(AbilityContext);
  const {refetch: refetchUser} = useFetchUser();
  const isAgency = useIsAgency();
  const agencyId = useAgencyId();
  const accessTokenFromStore = useTokenStore(state => state.access);
  const loggedInUserId = useUserStore(state => state.user.id);
  const registeringUserId = useRegistrationStore(state => state.userId);
  const company = useCompanyStore(state => state.company);
  const loggedInCompanyId = useSelectedCompanyId();
  const registeringCompanyId = useRegistrationStore(state => state.companyId);
  const registeringCompanyLogo = useRegistrationStore(state => state.companyLogo);
  const setRegistrationValue = useRegistrationStore(state => state.setValue);
  const companyId = useMemo(
    () => (isCompanyProfile ? company.id : (loggedInCompanyId ?? registeringCompanyId)),
    [company, loggedInCompanyId, isCompanyProfile, registeringCompanyId],
  );
  const userId = useMemo(
    () => (isCompanyProfile ? company?.user?.id : (loggedInUserId ?? registeringUserId)),
    [isCompanyProfile, company, loggedInUserId, registeringUserId],
  );
  const [file, setFile] = useState<File | null>(null);

  const onDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;
    setFile(acceptedFiles[0]);
  };

  const shownImage = useMemo(() => {
    return file ? URL.createObjectURL(file!) : (company.picture320 ?? company.picture ?? registeringCompanyLogo ?? "");
  }, [file, company.picture, company.picture320, registeringCompanyLogo]);

  const {getInputProps, getRootProps, isDragActive, isDragReject} = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
    },
  });

  const {mutate: uploadImages} = useMutation({
    mutationKey: [COMPANY_KEYS.mCompany(companyId!)[0], "brand_picture"],
    mutationFn: async () => {
      if (!file) return;
      const formData = new FormData();
      formData.append("picture", file);
      formData.append("picture640", file);
      formData.append("picture320", file);
      return await client.patch(COMPANY_KEYS.mCompany(companyId!)[0], formData, {
        headers: {
          Authorization: `Bearer ${accessTokenFromStore ?? accessTokenFromUrl}`,
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: response => {
      queryClient.invalidateQueries({queryKey: USER_KEYS.qUser(Number(userId)), exact: true});

      if (isAgency) {
        queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgencySubbrands(agencyId!)});
        refetchUser();
      }

      setRegistrationValue("companyLogo", response?.data?.picture);
    },
    onError: (error: AxiosError<{picture?: string[]}>) => handleErrorWithToast(error),
  });

  useEffect(() => {
    if (!file) return;
    uploadImages();
  }, [file, uploadImages]);

  if (abilityContext.cannot("update", "CompanyDetails")) {
    return !!shownImage ? (
      <div className="group border-primary-light-solid relative h-44 w-44 rounded-full border-2 shadow-md">
        <img src={shownImage} className="h-full w-full rounded-full object-cover" />
      </div>
    ) : null;
  }

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {!shownImage ? (
        <div
          className={clsxm(
            "group border-primary-dark-solid hover:border-primary-midnight h-44 w-44 rounded-full border-2 border-dashed bg-transparent object-cover transition-all duration-150",
            isDragReject && "border-danger-medium-red",
            isDragActive && !isDragReject && "border-primary-midnight border-2",
          )}>
          <div className="flex h-full w-full items-center justify-center">
            {isDragReject ? (
              <CrossIcon
                className={clsxm(
                  "border-primary-light-solid bg-primary-dark-solid group-hover:bg-primary-midnight flex h-12 w-12 items-center justify-center rounded-full border p-3.5 shadow-md transition-all duration-150",
                  isDragActive && !isDragReject && "bg-primary-midnight",
                  isDragReject && "border-danger-medium-red bg-danger-light-red",
                )}
              />
            ) : (
              <PlusIcon
                className={clsxm(
                  "border-primary-light-solid bg-primary-dark-solid group-hover:bg-primary-midnight flex h-12 w-12 items-center justify-center rounded-full border p-3.5 shadow-md transition-all duration-150",
                  isDragActive && !isDragReject && "bg-primary-midnight",
                  isDragReject && "border-danger-medium-red bg-danger-light-red",
                )}
              />
            )}
          </div>
        </div>
      ) : (
        <div
          className={clsxm(
            "group border-primary-light-solid hover:border-primary-midnight relative h-44 w-44 rounded-full border-2 shadow-md transition-all duration-150",
            isDragReject && "border-danger-medium-red",
            isDragActive && !isDragReject && "border-primary-midnight border-2",
          )}>
          <img src={shownImage} alt="" className="h-full w-full rounded-full object-cover" />
          <div
            className={clsxm(
              "border-primary-light-solid group-hover:border-primary-midnight absolute top-0 right-0 flex h-12 w-12 items-center justify-center rounded-full border bg-white p-3 shadow-lg transition-all duration-150",
              isDragActive && !isDragReject && "border-primary-midnight bg-primary-midnight",
              isDragReject && "border-danger-medium-red bg-danger-light-red",
            )}>
            {isDragReject ? (
              <CrossIcon className="size-4 shrink-0" />
            ) : (
              <EditIcon
                className={
                  "h-[18px] w-[18px] transition-all duration-150 " +
                  (isDragActive && !isDragReject ? "text-white" : "text-primary-dark-solid")
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(BrandLogo);
